import React from 'react';
import { ActivityButton } from './ActivityButton';
import type { DiaryField, DiaryActivity } from './DiaryDayPage';
import { cn } from '../primitives';

export interface FieldSetModalProps {
  /** Whether the modal is open */
  isOpen: boolean;
  /** Callback to close the modal */
  onClose: () => void;
  /** The fieldset being displayed */
  fieldSet: {
    id: string;
    name: string;
    fields: DiaryField[];
  };
  /** Activities for the fieldset fields */
  activities: Map<string, DiaryActivity>;
  /** Callback when a field is clicked */
  onFieldClick: (fieldSetId: string, fieldId?: string) => void;
  /** Icon base URL for rendering icons */
  iconBaseUrl?: string;
}

/**
 * FieldSetModal - Modal for selecting a specific field in a fieldset
 *
 * Shows all fields in a fieldset for selection via long-press
 */
export function FieldSetModal({
  isOpen,
  onClose,
  fieldSet,
  activities,
  onFieldClick,
  iconBaseUrl = '/assets',
}: FieldSetModalProps) {
  // Handle backdrop click
  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  // Handle escape key
  React.useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fieldset-modal-backdrop"
        onClick={handleBackdropClick}
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          backdropFilter: 'blur(4px)',
          zIndex: 1055,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {/* Modal content */}
        <div
          className="fieldset-modal-content"
          style={{
            backgroundColor: '#fff',
            borderRadius: 8,
            padding: 20,
            maxWidth: 500,
            width: '90%',
            maxHeight: '80vh',
            overflow: 'auto',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1), 0 2px 4px rgba(0, 0, 0, 0.06)',
            zIndex: 1070,
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div
            style={{
              marginBottom: 16,
              paddingBottom: 12,
              borderBottom: '1px solid #e5e7eb',
            }}
          >
            <h5 style={{ margin: 0, fontSize: 18, fontWeight: 600 }}>
              {fieldSet.name}
            </h5>
          </div>

          {/* Fields grid */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(71px, 1fr))',
              gap: 10,
              justifyItems: 'center',
            }}
          >
            {fieldSet.fields.map((field) => {
              const activity = activities.get(field.id);
              const isActive = activity?.active || false;
              return (
                <ActivityButton
                  key={field.id}
                  id={field.id}
                  name={field.name}
                  icon={field.icon}
                  variant="toggle"
                  active={true} // Always show as active in modal to display colors
                  automated={activity?.automaticallyTriggered || false}
                  score={field.score}
                  hasGoal={field.hasGoal}
                  onClick={() => {
                    onFieldClick(fieldSet.id, field.id);
                    onClose();
                  }}
                  className={!isActive ? 'fieldset-modal__field--not-selected' : ''}
                />
              );
            })}
          </div>

          {/* Action buttons */}
          <div style={{ marginTop: 16, textAlign: 'center', display: 'flex', gap: 8, justifyContent: 'center' }}>
            <button
              onClick={() => {
                // To force deselection: activate the last field, then cycle to deselect
                const lastField = fieldSet.fields[fieldSet.fields.length - 1];
                if (lastField) {
                  // First, activate the last field
                  onFieldClick(fieldSet.id, lastField.id);
                  // Then, cycle past it to deselect (using setTimeout to ensure first call completes)
                  setTimeout(() => {
                    onFieldClick(fieldSet.id);
                  }, 100);
                }
                onClose();
              }}
              style={{
                padding: '8px 24px',
                border: '1px solid #d1d5db',
                borderRadius: 4,
                backgroundColor: '#fff',
                color: '#374151',
                fontSize: 14,
                fontWeight: 500,
                cursor: 'pointer',
              }}
            >
              Unselect
            </button>
            <button
              onClick={onClose}
              style={{
                padding: '8px 24px',
                border: '1px solid #d1d5db',
                borderRadius: 4,
                backgroundColor: '#fff',
                color: '#374151',
                fontSize: 14,
                fontWeight: 500,
                cursor: 'pointer',
              }}
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

export default FieldSetModal;
